import type { FileWithLatestFileVersion } from "schemas/generated/file";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import { PublishButton } from "@/features/next-gen/components/PublishButton";
import { useWebContainer } from "@/features/web-container/context";
import { WebContainerStatus } from "@/features/web-container/useWebContainerHook";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { History, RefreshCcw, SquareArrowOutUpRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import usePublishedSiteUrls from "../hooks/usePublishedSiteUrls";
import { useEditorStoreActions, usePanelView } from "../store/editorStore";
import DebugPanel from "./navbar/DebugPanel";
import EditablePageNameWithDropdown from "./navbar/EditablePageNameWithDropdown";
import PageSettingsMenu from "./navbar/PageSettingsMenu";
import ResponsiveControls from "./navbar/ResponsiveControls";

type Props = {
  page: FileWithLatestFileVersion;
};

function PageRenderControls({ page }: Props) {
  const { setStatus } = useWebContainer();
  const projectId = useCurrentProjectId();
  const [isPageSelectionPopoverOpen, setIsPageSelectionPopoverOpen] =
    React.useState(false);

  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const { restart } = useWebContainer();
  const [_searchParams, setSearchParams] = useSearchParams();

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: page.pageRoute ? [page.pageRoute] : [],
  });

  const publishedSiteUrl = publishedSiteUrls[0];
  return (
    <div className="flex flex-row items-center justify-between gap-2 p-2 border-b-0.5 border-border w-full">
      <div className="flex items-center gap-2">
        <EditablePageNameWithDropdown
          page={page}
          projectId={projectId ?? ""}
          isPageSelectionPopoverOpen={isPageSelectionPopoverOpen}
          setIsPageSelectionPopoverOpen={setIsPageSelectionPopoverOpen}
          onPageSelect={(selectedPage) => {
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set("pageId", selectedPage.id);
              return newParams;
            });
            setIsPageSelectionPopoverOpen(false);
          }}
        />
        <IconButton
          variant="secondary"
          size="base"
          UNSAFE_className={twMerge(
            panelView === "history" &&
              "bg-primary/10 border border-primary hover:bg-primary/10",
          )}
          icon={
            <History
              className={twMerge(
                "text-muted",
                panelView === "history" && "text-primary",
              )}
              size={16}
            />
          }
          onClick={() =>
            setPanelView(panelView === "history" ? "chat" : "history")
          }
        />
      </div>
      <div className="flex items-center gap-2">
        <ResponsiveControls />
      </div>
      <div className="flex items-center gap-2">
        <DebugPanel />
        <Button
          onClick={() => {
            setStatus(WebContainerStatus.Booting);
            restart();
          }}
          variant="secondary"
          size="base"
          layoutClassName="w-8 h-8 min-w-8"
          tooltipText="Refresh"
        >
          <RefreshCcw size={16} />
        </Button>
        <PageSettingsMenu />
        <Button
          variant="secondary"
          size="base"
          to={publishedSiteUrl}
          target="_blank"
          layoutClassName="w-full flex-1"
          title={publishedSiteUrl}
          disabled={!Boolean(page?.lastPublishedFileVersionId)}
        >
          <div className="flex items-center gap-2">
            <SquareArrowOutUpRight size={16} /> View Live
          </div>
        </Button>
        <PublishButton
          routeType="page"
          publishedSiteUrl={publishedSiteUrl}
          fileIdsToPublish={[page.id]}
          isViewableLive={Boolean(page?.lastPublishedFileVersionId)}
        />
      </div>
    </div>
  );
}

export default PageRenderControls;
